import { Component, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatOption } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { State } from 'app/shared/models/state';
import { SubjectType } from 'app/shared/models/subject-type';
import { Subscription } from 'rxjs';
import { StateService } from 'app/shared/services/state.service';
import { SubjectTypeService } from 'app/shared/services/subject-type.service';
import { CountWidgetSettings } from 'app/shared/models/count-widget-settings';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { OrganisationService } from 'app/shared/services/organisation.service';
import { Organisation } from 'app/shared/models/organisations';
import { StatusService } from 'app/shared/services/status.service';
import { Status } from 'app/shared/models/status';
import { LabelService } from '../../label.service';
import { UserService } from 'app/shared/services/user.service';
import { Utilities } from 'app/shared/helpers/utilities';
import { Category, Lookup } from 'app/shared/models/lookup-category';
import { CategoryService } from 'app/shared/services/category.service';
import { LookupService } from 'app/shared/services/lookup.service';
import { environment } from 'environments/environment';
import { User } from 'app/shared/models/user';

@Component({
  selector: 'subject-count-widget-settings',
  templateUrl: './subject-count-widget-settings.component.html',
  styleUrls: ['./subject-count-widget-settings.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SubjectCountWidgetSettingsComponent implements OnInit, OnDestroy {

  form: UntypedFormGroup;
  onStatesChanged: Subscription;
  onStatusesChanged: Subscription;
  onTypesChanged: Subscription;
  onCategoriesChanged: Subscription;
  onCaseTypesChanged: Subscription;
  onOrganisationsChanged: Subscription;
  activeUsers: Subscription;
  valueChanges: Subscription;

  contexts = this.labelService.CONTEXTS;
  contextGeneral = this.labelService.CONTEXTS;
  contextUsers = [];
  types: SubjectType[] = [];
  states: State[] = [];
  statuses: Status[] = [];

  caseTypes: Lookup[] = [];
  categories: Category[] = [];
  extraCategories: Category[] = [];

  organisations: Organisation[] = [];
  settings: CountWidgetSettings;
  displayExtra = false;
  color: string;
  originalColor: string;
  organisationLabel = 'Organisation';
  hasOrganisation = false;
  showOrganisations = false;

  labelChanged = false;
  titleChanged = false;
  extraLabelChanged = false;
  prevContextValue: any;
  dlgTitle = 'Subject Count Widget Settings';
  currentSubjectTypeId = -1;
  currentExtraSubjectTypeId = -1;
  organisaitonReadonly: boolean = false;

  labelMaxLength = this.labelService.MAXLENGTH;
  titleMaxLength = this.labelService.MAXLENGTH;

  @ViewChild('allStatus') private allStatus: MatOption;
  @ViewChild('allExtraStatus') private allExtraStatus: MatOption;

  @ViewChild('allCategory') private allCategory: MatOption;
  @ViewChild('allExtraCategory') private allExtraCategory: MatOption;

  @ViewChild('allCaseType') private allCaseType: MatOption;
  @ViewChild('allExtraCaseType') private allExtraCaseType: MatOption;

  constructor(
    public dialogRef: MatDialogRef<SubjectCountWidgetSettingsComponent>,
    private stateService: StateService,
    private statusService: StatusService,
    private subjectTypeService: SubjectTypeService,
    private organisationService: OrganisationService,
    private labelService: LabelService,
    private userService: UserService,

    private lookupService: LookupService,
    private categoriesService: CategoryService,

    @Inject(MAT_DIALOG_DATA) public data: CountWidgetSettings,
    private fb: UntypedFormBuilder) {

      this.settings = data || new CountWidgetSettings();
      if (this.userService.getCurrentUser().organisationId && this.settings.organisationId==undefined)
        this.settings.organisationId = this.userService.getCurrentUser().organisationId;

    this.settings.color = this.settings.color || '#f44336'; // 'rgb(244, 67, 54)';

    this.currentSubjectTypeId = this.settings.typeId;
    this.currentExtraSubjectTypeId = this.settings.extraTypeId;

    switch (this.settings.showFlags) {
      case 1: this.dlgTitle = 'Subject Count by State Settings';
        break;
      case 2: this.dlgTitle = 'Subject Count by Status Settings';
        break;
      case 4: this.dlgTitle = 'Subject Count by Case Type Settings';
        break;
      case 8: this.dlgTitle = 'Subject Count by Category Settings';
        break;
    }
  }

  ngOnInit() {
    this.onStatesChanged = this.stateService.onChanged.subscribe((response) => {
      if (response.length > 0) {
        const states = response.filter((state) => ((state.hideRecord !== true) ||
          (this.settings && ((state.id === this.settings.stateId) || (state.id === this.settings.extraStateId)))));
        states.sort((a, b) => a.name.localeCompare(b.name));

        // add an 'all' option to states
        states.unshift({ id: -1, name: 'All' });
        this.states = states;

        // Remove FOI OnHold state from list if feature disabled.
        if (environment.enableFOIFeature == false)
          this.states = this.states.filter(i => i.uid != 'c8318c86-f0e2-4c09-86eb-04d97270aaaa');
      }
    });

    this.onCaseTypesChanged = this.lookupService.getAllByLookupTypeDescription('Case Type').subscribe((response) => {
      if (response.length > 0) {
        let caseTypes = response.filter((caseTypeItem) => (caseTypeItem.hideRecord !== true));
        caseTypes.sort((a, b) => a.description.localeCompare(b.description));
        let blankItem: Lookup = new Lookup();
        blankItem.id = -1;
        blankItem.description = 'Blank';
        caseTypes.unshift(blankItem);
        this.caseTypes = caseTypes;
      }
    });

    this.onCategoriesChanged = this.categoriesService.onChanged.subscribe((response) => {
      if (response.length > 0) {
        let categories = response.filter((categoryItem) => (categoryItem.hideRecord !== true && categoryItem.subjectType.hideRecord !== true));

        let categoriesList = categories.filter((i) => i.subjectTypeId == this.currentSubjectTypeId);
        categoriesList.sort((a, b) => a.description.localeCompare(b.description));
        categoriesList.unshift({ id: -1, description: 'Blank' });
        this.categories = categoriesList;

        let categoriesList2 = categories.filter((i) => i.subjectTypeId == this.currentExtraSubjectTypeId);
        categoriesList2.sort((a, b) => a.description.localeCompare(b.description));
        categoriesList2.unshift({ id: -1, description: 'Blank' });
        this.extraCategories = categoriesList2;
      }
    });

    this.onStatusesChanged = this.statusService.onChanged.subscribe((response) => {
      if (response.length > 0) {
        const statuses = response.filter((status) => ((status.hideRecord !== true) ||
          (this.settings && ((`,${this.settings.status}`).includes(`,${status.id}`) || (`,${this.settings.extraStatus}`).includes(`,${status.id}`)))));
        statuses.sort((a, b) => a.name.localeCompare(b.name));
        statuses.unshift({ id: -1, name: 'Blank' });
        this.statuses = statuses;

        if (environment.enableFOIFeature === false) {
          this.statuses = this.statuses.filter(i => {
            return i.state?.uid != 'c8318c86-f0e2-4c09-86eb-04d97270aaaa';
          });
        }
      }
    });

    this.onTypesChanged = this.subjectTypeService.onChanged.subscribe((response) => {
      if (response.length > 0) {
        const types = response.filter((type) => ((type.hideRecord !== true) ||
          (this.settings && ((type.id === this.settings.typeId) || (type.id === this.settings.extraTypeId)))));

        // MRD - see #9513.  Logically we shouldn't assign status to non primary subject,
        // but it appears we currently can, so don't filter here so we are at least consistent
        // if (this.settings.showFlags === 2) {
        //   types = types.filter((i) => i.primary);
        // }

        types.sort((a, b) => a.name.localeCompare(b.name));
        this.types = types;
      }
    });

    this.onOrganisationsChanged = this.organisationService.onChanged.subscribe((response) => {
      if (response.length > 0) {      
        var organisations = response.filter(
          (organisation) => organisation.hideRecord !== true || (this.settings && this.settings.organisationId && organisation.id === this.settings.organisationId)
        );

        if (this.userService.hasPriv('multiorgadmin')) {
          var userOrgs = this.userService.getCurrentUser()?.userOrganisations;
          if (environment.limitAccessToSubjectsByOrg && userOrgs && userOrgs.length > 0 && this.userService.getCurrentUser()) {
            organisations = organisations.filter(i => userOrgs.findIndex(j => j.organisationId == i.id) > -1);
          }
        }
        else if (environment.limitAccessToSubjectsByOrg && this.userService.getCurrentUser().role.toLocaleLowerCase() !='admin') {
          organisations = organisations.filter(i => i.id == this.userService.getCurrentUser()?.organisationId);
        }

        if (organisations.length > 1) {
          organisations.sort((a, b) => a.name.localeCompare(b.name));
          organisations = organisations.filter(i=>{ return (i.id!=-1)});
          organisations.unshift({ id: -1, name: 'All' });
        }
        this.organisations = organisations;
      }
    });


    const me: User = this.userService.getCurrentUser();
    this.organisaitonReadonly = (me.organisationId && me.organisationId > 0 && environment.limitAccessToSubjectsByOrg === true);
    if (this.organisaitonReadonly === true) {
      this.settings.organisationId = me.organisationId;
    }

    this.activeUsers = this.userService.getActiveUsers().subscribe((users) => {
      this.contextUsers = [];
      users.forEach((i) => {
        if ((i.id === me.id) || (i.managerId === me.id)) {
          this.contextUsers.push({ id: i.id, name: (i.id === me.id) ? 'Me' : i.fullName });
        }
      });
      this.contextUsers.sort((a, b) => a.name.localeCompare(b.name)).forEach((i) => {
        this.contexts.push(i);
      });
    });

    this.stateService.getAll();
    this.statusService.getAll();
    this.subjectTypeService.getAll();
    this.categoriesService.getAll();

    this.showOrganisations = this.organisationService.showOrganisations;
    if (this.showOrganisations) {
      this.organisationService.getAll();
      if (this.settings.typeId) {
        const subjectType = this.types.find((i) => i.id === this.settings.typeId);
        if (subjectType) {
          this.hasOrganisation = this.organisationService.hasOrganisation(subjectType);
          this.organisationLabel = this.organisationService.organisationLabel(subjectType.name);
        }
      }
      else {
        this.organisationLabel = this.organisationService.organisationLabel();
      }
    }

    // check title and labels
    if (this.settings.label) {
      const label = this.labelService.getDefaultCountLabel(this.settings).toUpperCase();
      this.labelChanged = !!(label && (label !== this.settings.label));
    }

    if (this.settings.extraLabel) {
      const extraLabel = this.labelService.getDefaultExtraCountLabel(this.settings);
      this.extraLabelChanged = !!(extraLabel && (extraLabel !== this.settings.extraLabel));
    }

    if (this.settings.title) {
      const title = this.labelService.getDefaultTitle(this.settings, undefined);
      this.titleChanged = !!(title && (title !== this.settings.title));
    }

    this.prevContextValue = this.settings.context;
    if (this.settings.context) {
      this.prevContextValue = this.labelService.getContext(this.settings.context);
    }

    this.form = this.fb.group({
      context: [this.prevContextValue, Validators.required],
      typeId: [this.settings.typeId, Validators.required],
      // stateId: [this.settings.stateId || -1, (!this.data.showStatus) ? Validators.required : undefined],
      // status: [this.settings.status || '', (this.data.showStatus) ? Validators.required : undefined],

      stateId: [this.settings.stateId || -1, (this.data.showFlags === 1) ? Validators.required : undefined],
      status: [this.settings.status || '', (this.data.showFlags === 2) ? Validators.required : undefined],
      caseType: [this.settings.caseType || '', (this.data.showFlags === 4) ? Validators.required : undefined],
      category: [this.settings.category || '', (this.data.showFlags === 8) ? Validators.required : undefined],
      referralCounts: [this.settings.referralCounts || false],

      label: [this.settings.label || ''],
      title: [this.settings.title || ''],
      organisationId: [this.settings.organisationId || -1, Validators.required],
      showExtra: [this.settings.showExtra],
      extraTypeId: [this.settings.extraTypeId],
      extraStateId: [this.settings.extraStateId || -1],
      extraStatus: [this.settings.extraStatus || ''],

      extraCaseType: [this.settings.extraCaseType || ''],
      extraCategory: [this.settings.extraCategory || ''],

      extraLabel: [this.settings.extraLabel || ''],
    });

    // set colour
    this.color = this.settings.color;
    this.originalColor = this.settings.color;

    this.formControlValueChanged();
    this.patchTitleAndLabels(this.settings);

    Utilities.patchCheckboxAria();
  }

  ngOnDestroy() {
    this.onStatesChanged?.unsubscribe();
    this.onStatusesChanged?.unsubscribe();
    this.onTypesChanged?.unsubscribe();
    this.onOrganisationsChanged?.unsubscribe();
    this.onOrganisationsChanged?.unsubscribe();
    this.activeUsers?.unsubscribe();
    this.onCategoriesChanged?.unsubscribe();
    this.valueChanges?.unsubscribe();
  }

  formControlValueChanged() {
    const extraCategoryControl = this.form.get('extraCategory');
    const extraTypeIdControl = this.form.get('extraTypeId');
    const extraStateIdControl = this.form.get('extraStateId');
    const extraStatusControl = this.form.get('extraStatus');
    const showExtraControl = this.form.get('showExtra');

    if (
      extraCategoryControl &&
      extraTypeIdControl &&
      extraStateIdControl &&
      extraStatusControl &&
      showExtraControl
    ) {
      this.valueChanges = showExtraControl.valueChanges.subscribe((val) => {
        if (val) {
          extraTypeIdControl.setValidators([Validators.required]);
          // if (!this.data.showStatus) {extraStateIdControl.setValidators([Validators.required]); }
          // if (this.data.showStatus) {extraStatusControl.setValidators([Validators.required]); }

          if (this.data.showFlags == 1) { extraStateIdControl.setValidators([Validators.required]); }
          if (this.data.showFlags == 2) { extraStatusControl.setValidators([Validators.required]); }
          if (this.data.showFlags == 8) { extraCategoryControl.setValidators([Validators.required]); }

          // MRD - delay the hiding of the controls just long enough for the validation change to take effect
          this.displayExtra = true;
          setTimeout(() => { this.displayExtra = false; }, 100);
        }
        else {
          extraCategoryControl.clearValidators();
          extraTypeIdControl.clearValidators();
          extraStateIdControl.clearValidators();
          extraStatusControl.clearValidators();

          extraCategoryControl.setErrors(null);
          extraTypeIdControl.setErrors(null);
          extraStateIdControl.setErrors(null);
          extraStatusControl.setErrors(null);
        }
        extraCategoryControl.updateValueAndValidity();
        extraTypeIdControl.updateValueAndValidity();
        extraStateIdControl.updateValueAndValidity();
        extraStatusControl.updateValueAndValidity();
      });
    }
  }

  onColorChange(color) {
    if (color && (this.originalColor !== color)) {
      this.form.markAsDirty();
    }
  }

  contextChange(ev: MatSelectChange) {
    const val = ev.value;
    if (val && Array.isArray(val)) {
      const newValues = (this.prevContextValue && (this.prevContextValue.length > 0)) ? val.filter((i) => !this.prevContextValue.includes(i)) : val;
      const cValues = newValues.filter((i) => i < 1);
      const patchValue = (cValues.length > 0) ? [cValues[0]] : val.filter((i) => i > 0);
      this.form.controls.context.patchValue(patchValue);
      this.prevContextValue = patchValue;
    } else {
      this.prevContextValue = ev.value;
    }
    this.selectionChange(ev);
  }

  subjectTypeChange(ev: MatSelectChange) {
    const val = ev.value;
    if (this.showOrganisations) {
      const subjectType = this.types.find((i) => i.id === val);
      if (subjectType) {
        this.hasOrganisation = this.organisationService.hasOrganisation(subjectType);
        this.organisationLabel = this.organisationService.organisationLabel(subjectType.name);
      }
    }

    this.currentSubjectTypeId = ev.value;
    this.form.controls.category.setValue(undefined);
    this.categoriesService.getAll();

    this.selectionChange(ev);
  }

  subjectExtraTypeChange(ev: MatSelectChange) {
    const val = ev.value;
    this.currentExtraSubjectTypeId = ev.value;
    this.form.controls.extraCategory.setValue(undefined);
    this.categoriesService.getAll();

    this.selectionChange(ev);
  }

  markLabelAsChanged() {
    this.labelChanged = true;
  }

  markTitleAsChanged() {
    this.titleChanged = true;
  }

  markExtraLabelAsChanged() {
    this.extraLabelChanged = true;
  }

  selectionChange(ev: MatSelectChange) {
    const data: CountWidgetSettings = this.form.getRawValue();
    this.patchTitleAndLabels(data);
  }

  patchTitleAndLabels(settings: CountWidgetSettings) {
    if ((!this.labelChanged) && (settings.stateId || settings.status)) {
      this.form.controls.label.patchValue(this.labelService.getDefaultCountLabel(settings).toUpperCase());
    }

    if (!this.titleChanged) {
      this.form.controls.title.patchValue(this.labelService.getDefaultTitle(settings, undefined));
    }

    if ((!this.extraLabelChanged) && settings.showExtra && (settings.extraStateId || settings.extraStatus)) {
      this.form.controls.extraLabel.patchValue(this.labelService.getDefaultExtraCountLabel(settings));
    }
  }

  openedStatusChange(opened: boolean) {
    if (this.allStatus) {
      if ((opened) && this.form.controls.status.value && this.statuses && (this.form.controls.status.value.length === this.statuses.length)) {
        this.allStatus.select();
      }
      else {
        this.allStatus.deselect();
      }
    }
  }

  openedExtraStatusChange(opened: boolean) {
    if (this.allExtraStatus) {
      if ((opened) && this.form.controls.extraStatus.value && this.statuses && (this.form.controls.extraStatus.value.length === this.statuses.length)) {
        this.allExtraStatus.select();
      }
      else {
        this.allExtraStatus.deselect();
      }
    }
  }

  toggleStatusAllSelection() {
    this.form.controls.status.patchValue(this.allStatus.selected ? [...this.statuses.map((item) => item.id), 0] : []);
  }

  toggleExtraStatusAllSelection() {
    this.form.controls.extraStatus.patchValue(this.allExtraStatus.selected ? [...this.statuses.map((item) => item.id), 0] : []);
  }

  toggleStatusPerOne() {
    if (this.allStatus) {
      if (this.allStatus.selected) {
        this.allStatus.deselect();
        return false;
      }
      if (this.form.controls.status.value.length === this.statuses.length) {
        this.allStatus.select();
      }
    }
  }

  toggleExtraStatusPerOne() {
    if (this.allExtraStatus) {
      if (this.allExtraStatus.selected) {
        this.allExtraStatus.deselect();
        return false;
      }
      if (this.form.controls.extraStatus.value.length === this.statuses.length) {
        this.allExtraStatus.select();
      }
    }
  }

  openedCategoryChange(opened: boolean) {
    if (this.allCategory) {
      if ((opened) && this.form.controls.category.value && this.categories && (this.form.controls.category.value.length === this.categories.length)) {
        this.allCategory.select();
      }
      else {
        this.allCategory.deselect();
      }
    }
  }

  openedExtraCategoryChange(opened: boolean) {
    if (this.allExtraCategory) {
      if ((opened) && this.form.controls.extraCategory.value && this.categories && (this.form.controls.extraCategory.value.length === this.categories.length)) {
        this.allExtraCategory.select();
      }
      else {
        this.allExtraCategory.deselect();
      }
    }
  }

  toggleCategoryAllSelection() {
    this.form.controls.category.patchValue(this.allCategory.selected ? [...this.categories.map((item) => item.id), 0] : []);
  }

  toggleExtraCategoryAllSelection() {
    this.form.controls.extraCategory.patchValue(this.allExtraCategory.selected ? [...this.extraCategories.map((item) => item.id), 0] : []);
  }

  toggleCategoryPerOne() {
    if (this.allCategory) {
      if (this.allCategory.selected) {
        this.allCategory.deselect();
        return false;
      }
      if (this.form.controls.category.value.length === this.categories.length) {
        this.allCategory.select();
      }
    }
  }

  toggleExtraCategoryPerOne() {
    if (this.allExtraCategory) {
      if (this.allExtraCategory.selected) {
        this.allExtraCategory.deselect();
        return false;
      }
      if (this.form.controls.extraCategory.value.length === this.extraCategories.length) {
        this.allExtraCategory.select();
      }
    }
  }

  openedCaseTypeChange(opened: boolean) {
    if (this.allCaseType) {
      if ((opened) && this.form.controls.caseType.value && this.caseTypes && (this.form.controls.caseType.value.length === this.caseTypes.length)) {
        this.allCaseType.select();
      }
      else {
        this.allCaseType.deselect();
      }
    }
  }

  openedExtraCaseTypeChange(opened: boolean) {
    if (this.allExtraCaseType) {
      if ((opened) && this.form.controls.extraCaseType.value && this.caseTypes && (this.form.controls.extraCaseType.value.length === this.caseTypes.length)) {
        this.allExtraCaseType.select();
      }
      else {
        this.allExtraCaseType.deselect();
      }
    }
  }

  toggleCaseTypeAllSelection() {
    this.form.controls.caseType.patchValue(this.allCaseType.selected ? [...this.caseTypes.map((item) => item.id), 0] : []);
  }

  toggleExtraCaseTypeAllSelection() {
    this.form.controls.extraCaseType.patchValue(this.allExtraCaseType.selected ? [...this.caseTypes.map((item) => item.id), 0] : []);
  }

  toggleCaseTypePerOne() {
    if (this.allCaseType) {
      if (this.allCaseType.selected) {
        this.allCaseType.deselect();
        return false;
      }
      if (this.form.controls.caseType.value.length === this.caseTypes.length) {
        this.allCaseType.select();
      }
    }
  }

  toggleExtraCaseTypePerOne() {
    if (this.allExtraCaseType) {
      if (this.allExtraCaseType.selected) {
        this.allExtraCaseType.deselect();
        return false;
      }
      if (this.form.controls.extraCaseType.value.length === this.caseTypes.length) {
        this.allExtraCaseType.select();
      }
    }
  }

  save() {
    const data = this.form.getRawValue();
    if (data.label === this.labelService.getDefaultCountLabel(data)) {
      data.label = undefined;
    }
    if (data.title === this.labelService.getDefaultTitle(data, undefined)) {
      data.title = undefined;
    }
    if (data.showExtra && (data.extraLabel === this.labelService.getDefaultExtraCountLabel(data))) {
      data.extraLabel = undefined;
    }
    if (!this.hasOrganisation) {
      data.organisationId = -1;
    }
    data.color = this.color;
    this.dialogRef.close({ data });
  }
}
